import React, { useCallback, useEffect, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { MailIcon, MailOpenIcon, ShuffleIcon } from "lucide-react";
import { DrawNav } from "../components/DrawNav";
import { RevealOverlay } from "../components/RevealOverlay";
import { SubjectList } from "../components/SubjectList";
import { CopyButton } from "../components/CopyButton";
import { Envelope } from "../components/Envelope";
import { SubjectLetter } from "../components/SubjectLetter";
import { useReveal } from "../hooks/useReveal";
import { subjects } from "../data/subjects";
import { groups } from "../data/groups";
import { assignmentsToText, drawSubjects, TEAM_COUNT, unassignedSubjects } from "../utils/subjects";
import type { SubjectAssignment } from "../types/subject";
const STORAGE_KEY = "subject-draw-v1";
function loadAssignments(): SubjectAssignment[] | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SubjectAssignment[];
    return Array.isArray(parsed) && parsed.length === TEAM_COUNT ? parsed : null;
  } catch {
    return null;
  }
}
export function SubjectDraw() {
  const [initial] = useState(loadAssignments);
  const [assignments, setAssignments] = useState<SubjectAssignment[] | null>(initial);
  const [round, setRound] = useState(initial ? 1 : 0);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const reveal = useReveal(TEAM_COUNT, !!initial);
  useEffect(() => {
    if (assignments) localStorage.setItem(STORAGE_KEY, JSON.stringify(assignments));
  }, [assignments]);
  const handleDraw = () => {
    if (assignments && !window.confirm("Replace the current track draw with a new one?")) return;
    setAssignments(drawSubjects());
    setRound(r => r + 1);
    reveal.reset();
    setOverlayOpen(true);
  };
  const closeOverlay = useCallback(() => setOverlayOpen(false), []);
  const revealed = assignments ? assignments.slice(0, reveal.revealedCount) : [];
  const current = assignments?.[reveal.current];
  const notAssigned = assignments && reveal.allRevealed ? unassignedSubjects(assignments) : [];
  return <div className="min-h-full w-full bg-paper" data-id="element-1095" data-mp="1yplnsjt27fdn">
      <main className="mx-auto max-w-6xl px-5 py-10 md:px-8 md:py-14" data-id="element-1096" data-mp="1ul46qse33g6k">
        <DrawNav data-id="element-1097" data-mp="1pmsy2kzizbpx" />
        <header className="mt-8 flex flex-col gap-6 md:flex-row md:items-end md:justify-between" data-id="element-1098" data-mp="1pncchrpyvhz7">
          <div data-id="element-1099" data-mp="2mdlc7d2wetp2">
            <h1 className="text-3xl font-semibold tracking-tight md:text-4xl" data-id="element-1100" data-mp="2mc0jlt059ktu">Track draw</h1>
            <p className="mt-2 max-w-lg text-[15px] text-muted" data-id="element-1101" data-mp="2ib8b51z3jjym">
              Groups 1 to 5 each draw one of 6 project tracks. One track stays unassigned.
            </p>
          </div>
          <button type="button" onClick={handleDraw} className="inline-flex items-center gap-2 self-start whitespace-nowrap rounded-xl bg-ink px-5 py-3 text-[15px] font-semibold text-white transition-[transform,background-color] duration-150 ease-out hover:bg-ink/90 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-2 focus-visible:ring-offset-paper md:self-auto" data-id="element-1102" data-mp="28m6v5bsjx2kn">
            <ShuffleIcon className="h-4 w-4" aria-hidden="true" data-id="element-1103" data-mp="3ijoj1u72488x" />
            {assignments ? "Redraw tracks" : "Draw tracks"}
          </button>
        </header>

        <div className="mt-10 grid gap-10 lg:grid-cols-12" data-id="element-1104" data-mp="3mml3zrk2kgg1">
          <section aria-labelledby="subjects-heading" className="lg:col-span-5" data-id="element-1105" data-mp="3rl4rhweba0ai">
            <div className="flex items-baseline justify-between pb-3" data-id="element-1106" data-mp="ds95e1ozgt0">
              <h2 id="subjects-heading" className="text-[15px] font-semibold" data-id="element-1107" data-mp="bz2hyr2wwl0">
                Project tracks
              </h2>
              <span className="text-sm tabular-nums text-muted" data-id="element-1108" data-mp="a3lgu3yprbrz">{subjects.length}</span>
            </div>
            <ol className="divide-y divide-line overflow-hidden rounded-xl border border-line bg-surface" data-id="element-1109" data-mp="e5ynculabdpt">
              {subjects.map((subject, i) => {
              const owner = revealed.find(a => a.subject === subject);
              const unused = notAssigned.includes(subject);
              return <li key={subject} className="flex items-center gap-3 px-3.5 py-3" data-id="element-1110" data-mp="1f67t8o8cyuy" data-mpi={i}>
                    <span className="w-4 text-right text-xs tabular-nums text-muted" aria-hidden="true" data-id="element-1111" data-mp="f7vrv7k77tpf">
                      {i + 1}
                    </span>
                    <span className={`min-w-0 flex-1 text-[15px] ${unused ? "text-muted line-through" : ""}`} data-id="element-1112" data-mp="aavo53atn31h">
                      {subject}
                    </span>
                    {owner && <span className="shrink-0 rounded-md bg-bryellow px-2 py-0.5 text-xs font-bold text-navy" data-id="element-1113" data-mp="3smintkddn69a">
                        Group {owner.team}
                      </span>}
                    {unused && <span className="shrink-0 text-xs text-muted" data-id="element-1114" data-mp="17w1ha7m766vl">Not assigned</span>}
                  </li>;
            })}
            </ol>

            <div className="flex items-baseline justify-between pb-3 pt-8" data-id="element-1115" data-mp="12wygizd92mtf">
              <h2 className="text-[15px] font-semibold" data-id="element-1116" data-mp="2ku1zg9kd7qot">Groups</h2>
              <span className="text-sm tabular-nums text-muted" data-id="element-1117" data-mp="2pufw6vgine7j">{groups.length}</span>
            </div>
            <ol className="divide-y divide-line overflow-hidden rounded-xl border border-line bg-surface" data-id="element-1118" data-mp="2buootdki8w2i">
              {groups.map(g => <li key={g.id} className="flex items-baseline gap-3 px-3.5 py-3" data-id="element-1119" data-mp="2gthz6x4hw6yg">
                  <span className="w-14 shrink-0 text-sm font-semibold" data-id="element-1120" data-mp="34k3zu3c66oyf">Group {g.id}</span>
                  <span className="flex min-w-0 flex-1 flex-wrap gap-x-3 gap-y-1" data-id="element-1121" data-mp="38j4c13056tcg">
                    {g.members.map(m => <span key={m.name} className="inline-flex items-center gap-1.5 text-[15px]" data-id="element-1122" data-mp="2uqwuujlcdayv">
                        <span className={`h-2 w-2 rounded-full ${m.school === "link" ? "bg-brgreen" : "bg-saffron"}`} aria-hidden="true" data-id="element-1123" data-mp="2zpzpda3p3ysl" />
                        {m.name}
                      </span>)}
                  </span>
                </li>)}
            </ol>
          </section>

          <section aria-labelledby="results-heading" className="lg:col-span-7" data-id="element-1124" data-mp="10o6pgi96b5s">
            <h2 id="results-heading" className="pb-3 text-[15px] font-semibold" data-id="element-1125" data-mp="3s4wjoy5o7sv1">
              Assignments
            </h2>
            {!assignments ? <div className="flex min-h-[360px] flex-col items-center justify-center rounded-2xl border border-dashed border-line px-6 text-center" data-id="element-1126" data-mp="3j59fk4lk48ya">
                <MailIcon className="h-6 w-6 text-muted" aria-hidden="true" data-id="element-1127" data-mp="3ed824fcnkc6o" />
                <p className="mt-4 text-[15px] font-medium" data-id="element-1128" data-mp="39grgpswu8bxa">No tracks drawn yet</p>
                <p className="mt-1 max-w-xs text-sm text-muted" data-id="element-1129" data-mp="34hejce8cbmgg">
                  Draw to seal a track for each group, then open the envelopes from Group 1 to Group 5.
                </p>
              </div> : <>
                <div className="bg-waves flex flex-wrap items-center justify-between gap-4 rounded-2xl px-6 py-5 text-white" data-id="element-1130" data-mp="2vgqeoa4u6yg8">
                  <div data-id="element-1131" data-mp="1ezhm69z70c4w">
                    <p className="text-[15px] font-semibold" data-id="element-1132" data-mp="1eyq481yiqcx3">
                      {reveal.allRevealed ? "All envelopes opened" : `${reveal.revealedCount} of ${TEAM_COUNT} envelopes opened`}
                    </p>
                    <p className="text-sm text-white/60" data-id="element-1133" data-mp="1ovb8um6mlm0e">Tracks saved in this browser</p>
                  </div>
                  <button type="button" onClick={() => {
                if (reveal.allRevealed) reveal.reset();
                setOverlayOpen(true);
              }} className="inline-flex items-center gap-2 whitespace-nowrap rounded-xl bg-bryellow px-4 py-2.5 text-sm font-bold text-navy transition-[transform,background-color] duration-150 ease-out hover:bg-[#ffd633] active:scale-[0.97]" data-id="element-1134" data-mp="w900uqgowitk">
                    <MailOpenIcon className="h-4 w-4" aria-hidden="true" data-id="element-1135" data-mp="330kh8j6ygrq9" />
                    {reveal.allRevealed ? "Watch again" : reveal.revealedCount > 0 ? "Continue reveal" : "Open envelopes"}
                  </button>
                </div>
                {revealed.length > 0 && <div className="mt-6" data-id="element-1136" data-mp="3byw4cb8m57dy">
                    <SubjectList assignments={revealed} data-id="element-1137" data-mp="371w7mqxnlgzi" />
                    {reveal.allRevealed && <div className="mt-4" data-id="element-1138" data-mp="3lyel6it88d1s">
                        <CopyButton text={assignmentsToText(assignments)} label="Copy tracks" data-id="element-1139" data-mp="3lxbyyo6umys7" />
                      </div>}
                  </div>}
              </>}
          </section>
        </div>
      </main>

      <AnimatePresence data-id="element-1140" data-mp="3qr7rhb4cftfz">
        {overlayOpen && assignments && current && <RevealOverlay title="Project track draw" subtitle="Link × Mahindra · Brazil meets India" total={TEAM_COUNT} reveal={reveal} envelopeKey={`${round}-${reveal.current}`} revealed={<SubjectList assignments={revealed} tone="dark" data-id="element-1142" data-mp="n80dynxxoz28" />} doneContent={<>
                <p className="text-sm text-white/60" data-id="element-1143" data-mp="s48zqk6mzqid">
                  Not assigned: <span className="text-white" data-id="element-1144" data-mp="d89o4zkbj88h">{unassignedSubjects(assignments).join(", ")}</span>
                </p>
                <CopyButton text={assignmentsToText(assignments)} label="Copy tracks" tone="dark" data-id="element-1145" data-mp="i6b5ng23izhu" />
              </>} onClose={closeOverlay} data-id="element-1141" data-mp="8k0g51t0a6c4">
            <Envelope label={`Group ${current.team}`} sublabel={groups.find(g => g.id === current.team)?.members.map(m => m.name.split(" ")[0]).join(" · ") ?? "Project track"} ariaLabel={`Open track envelope for Group ${current.team}`} open={reveal.open} onOpen={reveal.openEnvelope} burstKey={`${round}-${current.team}`} data-id="element-1146" data-mp="9898jblhgqzu">
              <SubjectLetter assignment={current} data-id="element-1147" data-mp="3rfycxawgd0ov" />
            </Envelope>
          </RevealOverlay>}
      </AnimatePresence>
    </div>;
}